import { useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import {
  Button,
  DataTable,
  EmptyState,
  ErrorNotice,
  PageHeading,
  Pagination,
  Panel,
  SearchInput,
  SectionHeader,
  SegmentedControl,
  SkeletonTable,
  useClientTable,
  type DataTableSort,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { hasPermission, PERMISSION_VIEW_SALES_DASHBOARD, readMyIdentity } from '@/lib/events';
import type { StatsInterval } from '@/lib/leads';
import { INTERVAL_LABELS, money } from '@/lib/report-window';
import { useIntervalChoice } from '@/lib/use-interval-choice';
import { SALES_QUERY_KEY, SALES_RANGE, compactMoney, percent, readSalesScorecard, salesTimeframeSearch, type SalesTimeframe } from '@/lib/sales';
import {
  NO_SPLIT,
  SALES_ANALYTICS_QUERY_KEY,
  SALES_GROUP,
  SALES_GROUP_LABEL,
  SALES_METRIC,
  SALES_RANGE_OPTIONS,
  rangeInterval,
  readSalesAnalytics,
  salesRangeWindow,
  salesTableRows,
  type SalesGroup,
  type SalesMetric,
  type SalesRangeKey,
  type SalesSplit,
  type SalesTableRow,
} from '@/lib/sales-analytics';
import { SalesSummaryTiles, SalesYoyStrip } from '@/components/sales-headline';
import { SalesCoveragePanel } from '@/components/SalesCoveragePanel';
import { SalesTrendPanel } from '@/components/SalesTrendPanel';

/** The route, named once (App.tsx, the nav and the old /bookings redirect use it). */
export const SALES_PATH = '/analytics/sales';

const DEFAULT_RANGE: SalesRangeKey = 'mtd';
/**
 * The scorecard feeds only the stale-deal count and the freshness line,
 * neither of which depends on the range: one fixed window, fetched once.
 */
const STALE_COUNT_TIMEFRAME: SalesTimeframe = { range: SALES_RANGE.thisMonth };
const TABLE_SORT_KEYS = ['key', 'bookings', 'booked', 'share', 'winRate', 'averageDeal', 'medianDaysToSign', 'open', 'pipeline'] as const;
const DEFAULT_SORT: DataTableSort = { key: 'booked', direction: 'desc' };

const count = (value: number): string => value.toLocaleString('en-US');
const days = (value: number | null): string => (value === null ? 'n/a' : count(Math.round(value)));

/**
 * Analytics → Sales, built around one question: are we up or down on last
 * year? First the year-over-year strip (month, quarter and year to date and
 * the last 3/6/12 months, as of today), then the picked range's velocity
 * levers against the same days last year, the trend chart (per period or
 * cumulative, bars or line, with 3/6/12-month trend lines at any grain and
 * last year), pipeline coverage of the goal, and the breakdown table by rep,
 * channel, program or new vs repeat. Every definition is the hq sales
 * scorecard's. Open to view_analytics or view_sales_dashboard; rep pages and
 * the stale queue need view_sales_dashboard.
 *
 * @returns The sales page.
 */
export function SalesPage(): React.ReactElement {
  const navigate = useNavigate();
  const [range, setRange] = useState<SalesRangeKey>(DEFAULT_RANGE);
  const [interval, setIntervalChoice] = useIntervalChoice(rangeInterval(range));
  const [metric, setMetric] = useState<SalesMetric>(SALES_METRIC.booked);
  const [split, setSplit] = useState<SalesSplit>(NO_SPLIT);
  const [groupBy, setGroupBy] = useState<SalesGroup>(SALES_GROUP.rep);

  const span = useMemo(() => salesRangeWindow(range), [range]);
  const timeframe: SalesTimeframe = { range: SALES_RANGE.custom, from: span.from, to: span.to };
  const rangeLabel = SALES_RANGE_OPTIONS.find((option) => option.key === range)?.label ?? '';

  const identity = useQuery({ queryKey: ['my-identity'], queryFn: ({ signal }) => readMyIdentity(signal), staleTime: 5 * 60_000 });
  const canOpenReps = hasPermission(identity.data, PERMISSION_VIEW_SALES_DASHBOARD);

  const scorecard = useQuery({
    queryKey: SALES_QUERY_KEY.scorecard(STALE_COUNT_TIMEFRAME),
    queryFn: ({ signal }) => readSalesScorecard(STALE_COUNT_TIMEFRAME, signal),
  });
  const analyticsParams = { from: span.from, to: span.to, interval, split, groupBy };
  const analytics = useQuery({
    queryKey: SALES_ANALYTICS_QUERY_KEY.params(analyticsParams),
    queryFn: ({ signal }) => readSalesAnalytics(analyticsParams, signal),
    placeholderData: keepPreviousData,
  });

  const rows = useMemo(() => (analytics.data ? salesTableRows(analytics.data) : []), [analytics.data]);
  const table = useClientTable<SalesTableRow>({
    rows,
    searchText: (row) => row.key,
    sortKeys: TABLE_SORT_KEYS,
    defaultSort: DEFAULT_SORT,
  });

  const team = scorecard.data?.team;
  const repLink = (row: SalesTableRow): string | null => (canOpenReps && groupBy === SALES_GROUP.rep && row.ownerId ? `${SALES_PATH}/${row.ownerId}${salesTimeframeSearch(timeframe)}` : null);

  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="ANALYTICS"
        title="Sales"
        subtitle="Are we up on last year, and why. A booking is a signed contract at its contract total; every comparison is with the same days a year earlier."
        action={
          canOpenReps ? (
            <Button to={`${SALES_PATH}/stale`} variant="secondary">
              Review stale deals
              {team ? ` (${count(team.shouldCloseAllOwners)} to close)` : ''}
            </Button>
          ) : undefined
        }
      />

      {analytics.isError ? (
        <ErrorNotice title="Sales analytics unavailable" message={errorMessage(analytics.error)} />
      ) : !analytics.data ? (
        <SkeletonTable rows={10} />
      ) : (
        <>
          <SectionHeader title="Year over year" description="Booked as of today against the same days a year earlier, whatever the range below." />
          <SalesYoyStrip periods={analytics.data.yoy} />

          <div className="mt-8 mb-4 flex flex-wrap items-end gap-3">
            <SegmentedControl<SalesRangeKey>
              label="Range"
              value={range}
              onChange={setRange}
              options={SALES_RANGE_OPTIONS.map((option) => ({ key: option.key, label: option.label }))}
            />
            <SegmentedControl<StatsInterval>
              label="Interval"
              value={interval}
              onChange={setIntervalChoice}
              options={(Object.keys(INTERVAL_LABELS) as StatsInterval[]).map((key) => ({ key, label: INTERVAL_LABELS[key] }))}
            />
          </div>

          <SalesSummaryTiles
            current={analytics.data.summary.current}
            lastYear={analytics.data.summary.lastYear}
            velocityPerDay={analytics.data.velocityPerDay}
            openDeals={analytics.data.open.deals}
            metric={metric}
            onSelectMetric={setMetric}
          />

          <div className="mt-4">
            <SalesTrendPanel data={analytics.data} metric={metric} onMetricChange={setMetric} split={split} onSplitChange={setSplit} />
          </div>

          <div className="mt-4">
            <SalesCoveragePanel coverage={analytics.data.coverage} open={analytics.data.open} />
          </div>

          <div className="mt-8">
            <SectionHeader
              title={`By ${SALES_GROUP_LABEL[groupBy].toLowerCase()}`}
              description={`Won and lost in the range; open deals and pipeline as of today.${groupBy === SALES_GROUP.rep && canOpenReps ? ' Open a rep for their deals.' : ''}`}
              aside={
                <div className="flex flex-wrap items-center gap-3">
                  <SegmentedControl<SalesGroup>
                    label="Group by"
                    value={groupBy}
                    onChange={setGroupBy}
                    options={(Object.keys(SALES_GROUP_LABEL) as SalesGroup[]).map((key) => ({ key, label: SALES_GROUP_LABEL[key] }))}
                  />
                  <SearchInput
                    label={`Search by ${SALES_GROUP_LABEL[groupBy].toLowerCase()}`}
                    placeholder={SALES_GROUP_LABEL[groupBy]}
                    value={table.search}
                    onChange={(event) => table.setSearch(event.target.value)}
                    className="w-full min-w-48 sm:w-60"
                  />
                </div>
              }
            />
            {table.matched === 0 ? (
              <Panel>
                <EmptyState
                  title={rows.length === 0 ? 'No deals in this range' : 'Nothing matches'}
                  message={rows.length === 0 ? 'Try a longer range.' : 'Try a different search.'}
                />
              </Panel>
            ) : (
              <>
                <DataTable
                  headers={[
                    { label: SALES_GROUP_LABEL[groupBy], sortKey: 'key' },
                    { label: 'Bookings', sortKey: 'bookings' },
                    { label: 'Booked $', sortKey: 'booked' },
                    { label: 'Share', sortKey: 'share' },
                    { label: 'Win rate', sortKey: 'winRate' },
                    { label: 'Average deal', sortKey: 'averageDeal' },
                    { label: 'Days to sign', sortKey: 'medianDaysToSign' },
                    { label: 'Open now', sortKey: 'open' },
                    { label: 'Pipeline now', sortKey: 'pipeline' },
                  ]}
                  rightAlignedHeaders={[1, 2, 3, 4, 5, 6, 7, 8]}
                  sort={table.sort}
                  onSort={table.onSort}
                  minWidth={980}
                  cards={table.pageRows.map((row) => {
                    const link = repLink(row);
                    return {
                      key: row.key,
                      title: row.key,
                      subtitle: `${count(row.bookings)} bookings · ${money.format(row.booked)}`,
                      ...(link ? { onOpen: () => navigate(link) } : {}),
                      fields: [
                        { label: 'Win rate', value: `${percent(row.winRate)} (${row.won} of ${row.won + row.lost})` },
                        { label: 'Average deal', value: row.averageDeal === null ? 'n/a' : compactMoney(row.averageDeal) },
                        { label: 'Days to sign', value: days(row.medianDaysToSign) },
                        { label: 'Share', value: percent(row.share) },
                        { label: 'Open now', value: count(row.open) },
                        { label: 'Pipeline now', value: compactMoney(row.pipeline) },
                      ],
                    };
                  })}
                >
                  {table.pageRows.map((row) => {
                    const link = repLink(row);
                    return (
                      <tr key={row.key} className="border-t border-portal-line align-middle">
                        <td className="p-3 text-portal-small font-bold">
                          {link ? (
                            <Link to={link} className="text-brand hover:underline">
                              {row.key}
                            </Link>
                          ) : (
                            <span className="text-portal-ink">{row.key}</span>
                          )}
                        </td>
                        <td className="p-3 text-right text-portal-small tabular-nums">{count(row.bookings)}</td>
                        <td className="p-3 text-right text-portal-small tabular-nums whitespace-nowrap">{money.format(row.booked)}</td>
                        <td className="p-3 text-right text-portal-small tabular-nums text-portal-muted">{percent(row.share)}</td>
                        <td className="p-3 text-right text-portal-small tabular-nums whitespace-nowrap">
                          <span className="block">{percent(row.winRate)}</span>
                          <span className="block text-portal-micro text-portal-muted">
                            {row.won} of {row.won + row.lost}
                          </span>
                        </td>
                        <td className="p-3 text-right text-portal-small tabular-nums whitespace-nowrap">
                          {row.averageDeal === null ? 'n/a' : compactMoney(row.averageDeal)}
                        </td>
                        <td className="p-3 text-right text-portal-small tabular-nums text-portal-muted">{days(row.medianDaysToSign)}</td>
                        <td className="p-3 text-right text-portal-small tabular-nums text-portal-muted">{count(row.open)}</td>
                        <td className="p-3 text-right text-portal-small tabular-nums whitespace-nowrap text-portal-muted">{compactMoney(row.pipeline)}</td>
                      </tr>
                    );
                  })}
                </DataTable>
                {table.totalPages > 1 ? (
                  <Pagination page={table.page} totalPages={table.totalPages} onChange={table.setPage} summary={table.summary} className="mt-3" />
                ) : null}
              </>
            )}
          </div>
        </>
      )}

      <p className="mt-6 text-portal-micro text-portal-muted">
        {rangeLabel ? `${rangeLabel}: ${span.from} to ${span.to}. ` : ''}A booking is Contract Signed or Signed - Postponed, on its signed
        date, at its contract total. Win rate is won ÷ (won + Closed Lost); unqualified and duplicate deals are not losses. Velocity
        is today&apos;s open deals × the range&apos;s win rate × average deal ÷ median days to sign. A trend line is the trailing months&apos; total rescaled to the
        bar&apos;s days (win rate and average deal: the trailing months&apos; own). Coverage counts open deals by their close date at the
        average proposed program. Days are Eastern time.
        {scorecard.data?.dataAsOf
          ? ` Salesforce data as of ${new Date(scorecard.data.dataAsOf).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}.`
          : ''}
      </p>
    </section>
  );
}
